import { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, MapPin, ChevronRight, ChevronDown } from 'lucide-react';
import { useMapWithProvider } from '@/hooks/useMapWithProvider';
import html2canvas from 'html2canvas';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { getStageGroup } from '@/lib/chargingStages';

const STAGES = [
  { id: 'acquisitie', label: 'Acquisitie', color: '#0091ff' },
  { id: 'realisatie', label: 'Realisatie', color: '#ff9100' },
  { id: 'operatie', label: 'Operatie', color: '#28a745' },
  { id: 'verloren', label: 'Verloren', color: '#e53935' },
];

export default function ChargingExport() {
  const location = useLocation();
  const { toast } = useToast();
  const mapRef = useRef<HTMLDivElement>(null);
  const { MapComponent } = useMapWithProvider();
  
  // Determine portal context
  const inCRM = location.pathname.startsWith('/crm');
  const portalType = inCRM ? 'crm' : 'charging';
  
  const [visibleLocationIds, setVisibleLocationIds] = useState<Set<string>>(new Set());
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(new Set(['verloren']));
  const [hideExactLocations, setHideExactLocations] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cityCenterCache, setCityCenterCache] = useState<Map<string, { lat: number, lng: number }>>(new Map());
  const [markerDisplayOptions, setMarkerDisplayOptions] = useState({
    showName: true,
    showCity: true,
    showStage: true,
    showAddress: false,
    showPower: false,
    showChargers: false,
  });
  const [markerSize, setMarkerSize] = useState<number>(25);
  const [showCompetitors, setShowCompetitors] = useState(false);
  const [showWishes, setShowWishes] = useState(false);
  const [selectedCompetitorOrgs, setSelectedCompetitorOrgs] = useState<Set<string>>(new Set());
  const [selectedWishOrgs, setSelectedWishOrgs] = useState<Set<string>>(new Set());
  const [transparentBackground, setTransparentBackground] = useState(false);

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['charging-all-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch competitor organisations and sites
  const { data: competitorOrgs = [] } = useQuery({
    queryKey: ['competitor-organisations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competitor_organisations')
        .select('*')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: competitorSites = [] } = useQuery({
    queryKey: ['competitor-sites'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competitor_sites')
        .select('*, organisation:competitor_organisations(name, accent_color)')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch wish organisations and sites
  const { data: wishOrgs = [] } = useQuery({
    queryKey: ['wish-organisations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wish_organisations')
        .select('*')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  const { data: wishSites = [] } = useQuery({
    queryKey: ['wish-sites'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wish_sites')
        .select('*, organisation:wish_organisations(name, accent_color)')
        .order('name');
      if (error) throw error;
      return data || [];
    },
  });

  // Initialize visible locations (all except 'verloren')
  useEffect(() => {
    if (locations.length > 0 && visibleLocationIds.size === 0) {
      const defaultVisible = locations
        .filter((loc: any) => getStageGroup(loc.stage) !== 'verloren')
        .map((loc: any) => loc.id);
      setVisibleLocationIds(new Set(defaultVisible));
    }
  }, [locations]);

  // Geocode city center
  const geocodeCity = async (cityName: string): Promise<{ lat: number, lng: number } | null> => {
    if (cityCenterCache.has(cityName)) {
      return cityCenterCache.get(cityName)!;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityName)},Netherlands&format=json&limit=1`,
        {
          headers: { 'User-Agent': 'Greenvision Charging Portal' }
        }
      );
      
      const data = await response.json();
      if (data && data.length > 0) {
        const coords = { 
          lat: parseFloat(data[0].lat), 
          lng: parseFloat(data[0].lon) 
        };
        
        setCityCenterCache(prev => new Map(prev).set(cityName, coords));
        return coords;
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Geocode cities when hideExactLocations is enabled
  useEffect(() => {
    if (hideExactLocations) {
      const citiesToGeocode = [...new Set(locations.map((loc: any) => loc.city).filter(Boolean))];
      
      citiesToGeocode.forEach(city => {
        if (!cityCenterCache.has(city)) {
          geocodeCity(city);
        }
      });
    }
  }, [hideExactLocations, locations]);

  const filteredLocations = useMemo(() => {
    return locations.filter((loc: any) => visibleLocationIds.has(loc.id));
  }, [locations, visibleLocationIds]);

  // Filter competitor/wish sites by selected organisations
  const filteredCompetitorSites = useMemo(() => {
    if (!showCompetitors) return [];
    return competitorSites.filter((site: any) => 
      selectedCompetitorOrgs.size === 0 || selectedCompetitorOrgs.has(site.organisation_id)
    );
  }, [competitorSites, showCompetitors, selectedCompetitorOrgs]);

  const filteredWishSites = useMemo(() => {
    if (!showWishes) return [];
    return wishSites.filter((site: any) => 
      selectedWishOrgs.size === 0 || selectedWishOrgs.has(site.organisation_id)
    );
  }, [wishSites, showWishes, selectedWishOrgs]);

  const mapSites = useMemo(() => {
    const chargingLocations = filteredLocations
      .filter((loc: any) => loc.latitude && loc.longitude)
      .map((loc: any) => {
        let finalLat = loc.latitude;
        let finalLng = loc.longitude;

        if (hideExactLocations && loc.city && cityCenterCache[loc.city]) {
          finalLat = cityCenterCache[loc.city].lat;
          finalLng = cityCenterCache[loc.city].lng;
        }

        return {
          id: loc.id,
          name: loc.name,
          address: hideExactLocations ? loc.city : (loc.address || loc.city),
          city: loc.city,
          latitude: finalLat,
          longitude: finalLng,
          type: loc.stage,
          site_type: loc.stage,
          category: 'charging',
          available_power_kw: loc.available_power_kw,
          development_size_chargers: loc.development_size_chargers,
          displayOptions: markerDisplayOptions,
        };
      });

    // Add competitor sites
    const competitors = filteredCompetitorSites
      .filter((site: any) => site.latitude && site.longitude)
      .map((site: any) => ({
        id: `competitor-${site.id}`,
        name: site.name,
        address: site.address || site.city,
        city: site.city,
        latitude: site.latitude,
        longitude: site.longitude,
        type: 'competitor',
        site_type: 'competitor',
        category: 'competitor',
        displayOptions: { showName: true, showCity: true },
      }));

    // Add wish sites
    const wishes = filteredWishSites
      .filter((site: any) => site.latitude && site.longitude)
      .map((site: any) => ({
        id: `wish-${site.id}`,
        name: site.name,
        address: site.address || site.city,
        city: site.city,
        latitude: site.latitude,
        longitude: site.longitude,
        type: 'wish',
        site_type: 'wish',
        category: 'wish',
        displayOptions: { showName: true, showCity: true },
      }));

    return [...chargingLocations, ...competitors, ...wishes];
  }, [filteredLocations, hideExactLocations, cityCenterCache, markerDisplayOptions, filteredCompetitorSites, filteredWishSites]);

  const getMarkerColor = (site: any) => {
    if (site.category === 'competitor') return '#e53935'; // Rood
    if (site.category === 'wish') return '#22c55e'; // Groen
    const stageGroup = getStageGroup(site.type);
    const stage = STAGES.find((s) => s.id === stageGroup);
    return stage?.color || '#666';
  };

  const toggleCollapseStage = (stageId: string) => {
    const newSet = new Set(collapsedStages);
    if (newSet.has(stageId)) {
      newSet.delete(stageId);
    } else {
      newSet.add(stageId);
    }
    setCollapsedStages(newSet);
  };

  const handleExport = async () => {
    if (!mapRef.current) {
      console.error('[ChargingExport] mapRef.current is null');
      return;
    }

    console.log('[ChargingExport] Starting export...', {
      transparentBackground,
      mapSitesCount: mapSites.length,
      mapRefDimensions: {
        width: mapRef.current.offsetWidth,
        height: mapRef.current.offsetHeight,
      },
    });

    setExporting(true);
    try {
      const canvas = await html2canvas(mapRef.current, {
        useCORS: true,
        allowTaint: false,
        scale: 2, // Higher resolution
        backgroundColor: transparentBackground ? null : '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          console.log('[ChargingExport] html2canvas cloned document');
        },
      });

      console.log('[ChargingExport] Canvas created:', {
        width: canvas.width,
        height: canvas.height,
      });

      const link = document.createElement('a');
      link.download = `charging-kaart-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast({ title: 'Export succesvol', description: 'Kaart gedownload als PNG' });
    } catch (error: any) {
      console.error('[ChargingExport] Export failed:', error);
      console.error('[ChargingExport] Error stack:', error?.stack);
      toast({
        title: 'Export mislukt',
        description: error?.message || 'Fout bij downloaden van kaart',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const locationsByStage = useMemo(() => {
    return STAGES.reduce((acc, stage) => {
      acc[stage.id] = locations.filter((loc: any) => getStageGroup(loc.stage) === stage.id);
      return acc;
    }, {} as Record<string, any[]>);
  }, [locations]);

  return (
    <div className="container mx-auto p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Download className="w-8 h-8" />
              Export Kaart
            </h1>
            <p className="text-muted-foreground">Download kaart met custom filters</p>
          </div>
          <Button onClick={handleExport} disabled={exporting}>
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporteren...' : 'Download PNG'}
          </Button>
        </div>

        {/* Map - Full width at top */}
        <Card className="p-4">
          <div 
            ref={mapRef} 
            className={`w-full h-[500px] rounded-lg overflow-hidden border ${transparentBackground ? 'transparent-map-bg' : ''}`}
          >
            <style>{`
              .transparent-map-bg .leaflet-tile-pane {
                opacity: 0 !important;
              }
              .transparent-map-bg {
                background: transparent !important;
              }
            `}</style>
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Laden...</p>
              </div>
            ) : mapSites.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-muted-foreground">Geen locaties om weer te geven</p>
              </div>
            ) : (
              <MapComponent
                sites={mapSites}
                getMarkerColor={getMarkerColor}
                portalType={portalType}
                markerSize={markerSize}
                maintainZoom={true}
              />
            )}
          </div>
        </Card>

        {/* Filters - 4 column grid below map */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Column 1: Location filters */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">Locaties</h3>
            {STAGES.map((stage) => {
              const stageLocations = locationsByStage[stage.id] || [];
              const allChecked = stageLocations.every((loc: any) => visibleLocationIds.has(loc.id));
              const someChecked = stageLocations.some((loc: any) => visibleLocationIds.has(loc.id));
              const isCollapsed = collapsedStages.has(stage.id);
              
              return (
                <div key={stage.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={allChecked}
                        ref={(el) => {
                          if (el) {
                            (el as any).indeterminate = someChecked && !allChecked;
                          }
                        }}
                        onCheckedChange={(checked) => {
                          const newSet = new Set(visibleLocationIds);
                          stageLocations.forEach((loc: any) => {
                            if (checked) {
                              newSet.add(loc.id);
                            } else {
                              newSet.delete(loc.id);
                            }
                          });
                          setVisibleLocationIds(newSet);
                        }}
                      />
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                      <span className="text-sm font-medium">{stage.label}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {stageLocations.length}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => toggleCollapseStage(stage.id)}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  
                  {!isCollapsed && (
                    <div className="ml-6 space-y-1 max-h-[200px] overflow-y-auto">
                      {stageLocations.map((loc: any) => (
                        <div key={loc.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`loc-${loc.id}`}
                            checked={visibleLocationIds.has(loc.id)}
                            onCheckedChange={(checked) => {
                              const newSet = new Set(visibleLocationIds);
                              if (checked) {
                                newSet.add(loc.id);
                              } else {
                                newSet.delete(loc.id);
                              }
                              setVisibleLocationIds(newSet);
                            }}
                          />
                          <Label 
                            htmlFor={`loc-${loc.id}`}
                            className="text-xs cursor-pointer"
                          >
                            {loc.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>

          {/* Column 2: Competitor & Wishes */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">Concurrentie & Wensen</h3>
            
            {/* Competitors toggle */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={showCompetitors}
                  onCheckedChange={(checked) => {
                    setShowCompetitors(checked as boolean);
                    if (!checked) setSelectedCompetitorOrgs(new Set());
                  }}
                />
                <div className="w-3 h-3 rounded-full bg-[#e53935]" />
                <span className="text-sm font-medium">Toon concurrentie</span>
              </Label>
              
              {showCompetitors && competitorOrgs.length > 0 && (
                <div className="ml-7 space-y-1 max-h-[150px] overflow-y-auto">
                  {competitorOrgs.map((org: any) => (
                    <Label key={org.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedCompetitorOrgs.has(org.id)}
                        onCheckedChange={(checked) => {
                          const newSet = new Set(selectedCompetitorOrgs);
                          if (checked) {
                            newSet.add(org.id);
                          } else {
                            newSet.delete(org.id);
                          }
                          setSelectedCompetitorOrgs(newSet);
                        }}
                      />
                      <span className="text-xs">{org.name}</span>
                    </Label>
                  ))}
                </div>
              )}
            </div>

            {/* Wishes toggle */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={showWishes}
                  onCheckedChange={(checked) => {
                    setShowWishes(checked as boolean);
                    if (!checked) setSelectedWishOrgs(new Set());
                  }}
                />
                <div className="w-3 h-3 rounded-full bg-[#22c55e]" />
                <span className="text-sm font-medium">Toon wensen</span>
              </Label>
              
              {showWishes && wishOrgs.length > 0 && (
                <div className="ml-7 space-y-1 max-h-[150px] overflow-y-auto">
                  {wishOrgs.map((org: any) => (
                    <Label key={org.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedWishOrgs.has(org.id)}
                        onCheckedChange={(checked) => {
                          const newSet = new Set(selectedWishOrgs);
                          if (checked) {
                            newSet.add(org.id);
                          } else {
                            newSet.delete(org.id);
                          }
                          setSelectedWishOrgs(newSet);
                        }}
                      />
                      <span className="text-xs">{org.name}</span>
                    </Label>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Column 3: Marker information */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">Marker Informatie</h3>
            <div className="space-y-2">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={markerDisplayOptions.showName}
                  onCheckedChange={(checked) => 
                    setMarkerDisplayOptions(prev => ({ ...prev, showName: checked as boolean }))
                  }
                />
                <span className="text-sm">Naam</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={markerDisplayOptions.showCity}
                  onCheckedChange={(checked) => 
                    setMarkerDisplayOptions(prev => ({ ...prev, showCity: checked as boolean }))
                  }
                />
                <span className="text-sm">Stad</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={markerDisplayOptions.showStage}
                  onCheckedChange={(checked) => 
                    setMarkerDisplayOptions(prev => ({ ...prev, showStage: checked as boolean }))
                  }
                />
                <span className="text-sm">Stage</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={markerDisplayOptions.showAddress}
                  onCheckedChange={(checked) => 
                    setMarkerDisplayOptions(prev => ({ ...prev, showAddress: checked as boolean }))
                  }
                />
                <span className="text-sm">Adres</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={markerDisplayOptions.showPower}
                  onCheckedChange={(checked) => 
                    setMarkerDisplayOptions(prev => ({ ...prev, showPower: checked as boolean }))
                  }
                />
                <span className="text-sm">Vermogen (kW)</span>
              </Label>
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={markerDisplayOptions.showChargers}
                  onCheckedChange={(checked) => 
                    setMarkerDisplayOptions(prev => ({ ...prev, showChargers: checked as boolean }))
                  }
                />
                <span className="text-sm">Aantal laders</span>
              </Label>
            </div>
          </Card>

          {/* Column 4: Options */}
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">Opties</h3>
            
            {/* Marker size slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Marker Grootte</span>
                <span className="text-sm text-muted-foreground">{markerSize}px</span>
              </div>
              <Slider
                value={[markerSize]}
                onValueChange={([value]) => setMarkerSize(value)}
                min={15}
                max={50}
                step={1}
                className="w-full"
              />
            </div>

            {/* Privacy toggle */}
            <div className="pt-3 border-t">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={hideExactLocations}
                  onCheckedChange={(checked) => setHideExactLocations(checked as boolean)}
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Verberg exacte locaties</span>
                  <span className="text-xs text-muted-foreground">Toon alleen stadcentra</span>
                </div>
              </Label>
            </div>
            
            {/* Transparent background toggle */}
            <div className="pt-3 border-t">
              <Label className="flex items-center justify-between cursor-pointer">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Transparante achtergrond</span>
                  <span className="text-xs text-muted-foreground">Verberg kaartachtergrond bij export</span>
                </div>
                <Switch
                  checked={transparentBackground}
                  onCheckedChange={setTransparentBackground}
                />
              </Label>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
